import { useCallback, useMemo, useRef, useState } from 'react';

// Multi-select for the Live grids, shared by the Classic and Zone twins so the
// two cannot drift apart in behaviour — only in skin.
//
// Selections are held as row indexes into the full `rows` array, not positions
// in the filtered list, so searching or re-sorting mid-selection keeps every
// item that was already ticked.
//
// `visible` is the currently filtered/sorted list of { row, realIndex, ... };
// `all` is every holding. Clicks and select-all work on what is on screen, but
// the selection is always resolved against `all`, so an item that scrolls out of
// a filter after being ticked is still in the group when the price is applied.
export default function useGroupPick(visible, all = visible) {
  const [on, setOn]   = useState(false);   // selection mode
  const [sel, setSel] = useState(() => new Set());
  const anchor = useRef(null);             // last index clicked, for shift-range

  const clear  = useCallback(() => { setSel(new Set()); anchor.current = null; }, []);
  const toggle = useCallback(mode => { setOn(mode); setSel(new Set()); anchor.current = null; }, []);

  // Click a card: plain click toggles one, shift-click takes everything between
  // it and the last card clicked — the usual file-list gesture, which is what
  // makes picking a dozen items out of a full armory bearable.
  const click = useCallback((realIndex, shiftKey) => {
    setSel(prev => {
      const next = new Set(prev);
      const from = anchor.current != null ? visible.findIndex(v => v.realIndex === anchor.current) : -1;
      const to   = visible.findIndex(v => v.realIndex === realIndex);
      if (shiftKey && from !== -1 && to !== -1) {
        const [lo, hi] = from < to ? [from, to] : [to, from];
        for (let i = lo; i <= hi; i++) next.add(visible[i].realIndex);
      } else if (next.has(realIndex)) {
        next.delete(realIndex);
      } else {
        next.add(realIndex);
      }
      return next;
    });
    anchor.current = realIndex;
  }, [visible]);

  const selectAll = useCallback(() => {
    setSel(new Set(visible.map(v => v.realIndex)));
    anchor.current = null;
  }, [visible]);

  // Everything the action bar needs to describe the selection before it opens
  // the modal. `entries` keeps the rows themselves so callers never have to look
  // an index back up against a list that may have changed underneath them.
  const picked = useMemo(() => {
    const entries = all.filter(v => sel.has(v.realIndex));
    const shown   = new Set(visible.map(v => v.realIndex));
    return {
      entries,
      count:  entries.length,
      cost:   entries.reduce((s, e) => s + (e.buy ?? 0), 0),
      market: entries.reduce((s, e) => s + (e.mkt ?? 0), 0),
      unpriced: entries.filter(e => e.mkt == null).length,
      hidden:   entries.filter(e => !shown.has(e.realIndex)).length,
    };
  }, [all, visible, sel]);

  return { on, setOn: toggle, sel, click, selectAll, clear, picked };
}
